import React, { useState } from 'react'
import { Card, Button, Badge, EmptyState } from './ui'
import { findCompanyDuplicateGroups, hasInternalDupes, dedupeContactsArray, planCompanyMerge } from '../lib/dedupe'

function Stat({ label, value, color }) {
  return (
    <div style={{ background: '#fff', border: '0.5px solid rgba(0,0,0,0.1)', borderRadius: 10, padding: '0.75rem 0.9rem', flex: '1 1 140px' }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || '#111' }}>{value}</div>
      <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{label}</div>
    </div>
  )
}

// Controlled, no-auto-delete duplicate cleanup for the contact database.
// Nothing changes until the user picks a keeper and confirms.
export default function DuplicateReview({ dbContacts = [], updateDbContact, deleteDbContact }) {
  const [notif, setNotif] = useState('')
  const [busy, setBusy] = useState(false)
  const [keepers, setKeepers] = useState({}) // groupKey -> keeperId

  const groups = findCompanyDuplicateGroups(dbContacts)
  const internalDupRows = dbContacts.filter(hasInternalDupes)
  const rowsAffected = groups.reduce((n, g) => n + g.rows.length, 0)

  const keeperFor = (g) => keepers[g.key] || [...g.rows].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''))[0].id

  const handleMerge = async (g) => {
    const keeperId = keeperFor(g)
    const plan = planCompanyMerge(g.rows, keeperId)
    const keeperRow = g.rows.find(r => r.id === keeperId)
    if (!window.confirm(
      `Gabungkan ${g.rows.length} baris "${g.name}" jadi 1?\n\n` +
      `Keeper: ${keeperRow.company} (agent ${keeperRow.agent || '—'})\n` +
      `Kontak setelah digabung: ${plan.mergedContactsCount}\n` +
      `${plan.deleteIds.length} baris duplikat akan dihapus.`
    )) return
    setBusy(true)
    try {
      await updateDbContact(plan.keeperId, plan.update)
      for (const id of plan.deleteIds) await deleteDbContact(id)
      setNotif(`"${g.name}" digabung — ${plan.mergedContactsCount} kontak, ${plan.deleteIds.length} baris dihapus.`)
    } catch (e) { setNotif('Error: ' + e.message) }
    finally { setBusy(false) }
  }

  const handleCleanInternal = async (row) => {
    const cleaned = dedupeContactsArray(row.contacts || [])
    const removed = (row.contacts || []).length - cleaned.length
    if (!window.confirm(`Bersihkan ${removed} kontak dobel di "${row.company}"?`)) return
    setBusy(true)
    try {
      await updateDbContact(row.id, { contacts: cleaned })
      setNotif(`"${row.company}" — ${removed} kontak dobel dibersihkan.`)
    } catch (e) { setNotif('Error: ' + e.message) }
    finally { setBusy(false) }
  }

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#111' }}>Duplicate Review</div>
        <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
          Tinjau & gabungkan data duplikat dengan aman. Tidak ada yang dihapus/digabung otomatis — kamu pilih keeper lalu konfirmasi.
        </div>
      </div>

      {notif && (
        <div style={{ background: '#E1F5EE', color: '#0F6E56', borderRadius: 8, padding: '9px 12px', fontSize: 12, marginBottom: 12 }}>
          {notif}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: '1rem' }}>
        <Stat label="Grup perusahaan duplikat" value={groups.length} color="#A32D2D" />
        <Stat label="Baris terdampak" value={rowsAffected} color="#854F0B" />
        <Stat label="Perusahaan dgn kontak dobel" value={internalDupRows.length} color="#185FA5" />
      </div>

      {/* Company-level duplicates */}
      <Card>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Perusahaan duplikat (nama sama)</div>
        {groups.length === 0 ? <EmptyState>Tidak ada perusahaan duplikat. Database bersih.</EmptyState> : (
          <div style={{ display: 'grid', gap: 12 }}>
            {groups.slice(0, 40).map(g => {
              const kid = keeperFor(g)
              return (
                <div key={g.key} style={{ border: '0.5px solid rgba(0,0,0,0.12)', borderRadius: 10, padding: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{g.name} <Badge variant="danger">{g.rows.length} baris</Badge></div>
                    <Button size="sm" variant="primary" disabled={busy} onClick={() => handleMerge(g)}>Gabungkan</Button>
                  </div>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Pilih baris yang dipertahankan (keeper):</div>
                  {g.rows.map(r => (
                    <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6, background: r.id === kid ? '#EAF2FB' : '#f9f9f7', marginBottom: 4, cursor: 'pointer', fontSize: 12 }}>
                      <input type="radio" name={'keep-' + g.key} checked={r.id === kid} onChange={() => setKeepers(k => ({ ...k, [g.key]: r.id }))} />
                      <span style={{ flex: 1 }}>
                        <strong>{r.company}</strong> · agent {r.agent || '—'} · {(r.contacts || []).length} kontak · status {r.status || '—'}
                        {r.source ? ` · ${r.source}` : ''}
                      </span>
                      {r.id === kid && <Badge variant="info">keeper</Badge>}
                    </label>
                  ))}
                </div>
              )
            })}
            {groups.length > 40 && <div style={{ fontSize: 12, color: '#888', textAlign: 'center' }}>Menampilkan 40 grup pertama dari {groups.length}. Gabungkan sebagian, sisanya akan muncul lagi.</div>}
          </div>
        )}
      </Card>

      {/* Internal duplicate contacts */}
      <div style={{ marginTop: '1rem' }}>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Kontak dobel di dalam 1 perusahaan</div>
          {internalDupRows.length === 0 ? <EmptyState>Tidak ada kontak dobel di dalam perusahaan.</EmptyState> : (
            internalDupRows.slice(0, 50).map(r => {
              const dupCount = (r.contacts || []).length - dedupeContactsArray(r.contacts || []).length
              return (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 0', borderBottom: '0.5px solid rgba(0,0,0,0.07)' }}>
                  <div style={{ fontSize: 12 }}>
                    <strong>{r.company}</strong> · agent {r.agent || '—'} · {(r.contacts || []).length} kontak
                    <span style={{ color: '#A32D2D' }}> ({dupCount} dobel)</span>
                  </div>
                  <Button size="sm" disabled={busy} onClick={() => handleCleanInternal(r)}>Bersihkan</Button>
                </div>
              )
            })
          )}
        </Card>
      </div>
    </div>
  )
}
